import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import { EmptyState, ErrorBanner, Modal, Pagination, TableSkeleton } from './ui';
import { FormulationDetailModal, answerText } from './FormulationDetail';
import { AdminFormulationList } from '../types/formulation';
import { formatDate } from '../utils/format';

/** Long free-text answers ("the smell of my grandmother's kitchen…") would blow
 *  the row height out; the full text is one click away in the detail panel. */
function truncate(s: string, n = 48): string {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/**
 * Every formulation that landed in one cluster, with the answers behind it.
 *
 * Opened from the Dashboard's cluster chart. Its own dialog rather than a page:
 * this is a "what is actually in that bar?" question, and the answer belongs
 * next to the bar that raised it.
 */
export function ClusterFormulationsModal({
  cluster,
  onClose,
}: {
  /** The cluster_label to filter on, exactly as the engine emitted it. */
  cluster: string;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminFormulationList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formulationId, setFormulationId] = useState<string | null>(null);

  // A different cluster is a different list — never show the last one's rows.
  useEffect(() => {
    setPage(1);
    setData(null);
  }, [cluster]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await formulationsApi.getFormulations({ cluster, page }));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [cluster, page]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data?.pagination.total ?? 0;

  return (
    <>
      <Modal
        open
        title={`${cluster} — All Formulations${total ? ` (${total})` : ''}`}
        onClose={onClose}
        size="xl"
        bodyClassName=""
      >
        {error ? (
          <div className="p-5">
            <ErrorBanner message={error} onRetry={load} />
          </div>
        ) : loading && !data ? (
          <TableSkeleton rows={5} />
        ) : !data || data.formulations.length === 0 ? (
          <EmptyState message="No formulations in this cluster" hint="Nothing has landed here yet" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">User</th>
                    <th className="th">Date</th>
                    <th className="th">Personality</th>
                    <th className="th">Comfort Memory</th>
                    <th className="th">Preferred Families</th>
                    <th className="th">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {data.formulations.map((f) => (
                    <tr key={f.id} className="transition-colors hover:bg-edge/20">
                      <td className="td">
                        {f.user_id ? (
                          <Link to={`/users/${f.user_id}`} className="font-medium text-gold hover:underline">
                            {f.display_name?.trim() || f.email || 'Unnamed'}
                          </Link>
                        ) : (
                          // The quiz runs without an account; sign-in is only
                          // asked for at payment.
                          <span className="text-sub">Not signed in</span>
                        )}
                      </td>
                      <td className="td text-sub">{formatDate(f.created_at)}</td>
                      <td className="td">{truncate(answerText(f.request?.personality_archetype), 28)}</td>
                      <td className="td text-sub">{truncate(answerText(f.request?.comfort_memory))}</td>
                      <td className="td text-sub">{truncate(answerText(f.request?.preferred_families), 32)}</td>
                      <td className="td">
                        <button
                          type="button"
                          className="btn-outline !px-2.5 !py-1 !text-xs"
                          onClick={() => setFormulationId(f.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.pagination.page} totalPages={data.pagination.pages} onPage={setPage} />
          </>
        )}
      </Modal>

      {formulationId && (
        <FormulationDetailModal id={formulationId} onClose={() => setFormulationId(null)} />
      )}
    </>
  );
}
